import type { UINotice } from '@sorrem-client/types';
import { useToast } from '@sorrem-client/ui/components';
import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import { useGameSelector } from '@sorrem-client/ui/hooks';
import { useEffect, useRef } from 'react';

/** How long a run notice shows: long enough to read a short line, short enough not to linger over play. */
export const RUN_NOTICE_TOAST_MS = 3000;

/** The notices not shown yet, marking them shown in `shownIds`. */
export function takeUnshownNotices(notices: readonly UINotice[], shownIds: Set<string>): UINotice[] {
    const unshown = notices.filter((notice) => !shownIds.has(notice.id));
    for (const notice of unshown) {
        shownIds.add(notice.id);
    }
    return unshown;
}

/**
 * Shows the run's notices (an action refused, an error) as toasts, each
 * once (#72); without them the player would never learn why an action
 * did nothing. Compact and brief, so they never cover or block play.
 */
export function useNoticeToasts(): void {
    const { bridge } = useGameplay();
    const notices = useGameSelector(bridge, (state) => state.notices);
    const { showToast } = useToast();
    const shownIdsRef = useRef(new Set<string>());

    useEffect(() => {
        for (const notice of takeUnshownNotices(notices, shownIdsRef.current)) {
            showToast({ message: notice.text, severity: notice.severity, compact: true, durationMs: RUN_NOTICE_TOAST_MS });
        }
    }, [notices, showToast]);
}
